import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ChevronLeft, ChevronRight, Plus, Users, Calendar, X, Clock, 
  AlertTriangle, Lightbulb, TrendingUp, DollarSign, Eye, UserPlus
} from 'lucide-react';
import { contractsAPI, staffAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const StaffScheduler = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [contracts, setContracts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [staffSuggestion, setStaffSuggestion] = useState(null);
  const [conflicts, setConflicts] = useState({});
  const [workload, setWorkload] = useState([]);
  const [showDayView, setShowDayView] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayViewData, setDayViewData] = useState(null);
  const [bulkAssignMode, setBulkAssignMode] = useState(false);
  const [bulkAssignments, setBulkAssignments] = useState([]);
  const { toast } = useToast();

  const [assignmentForm, setAssignmentForm] = useState({
    staff_id: '',
    role: '',
    start_time: '',
    end_time: '',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [contractsData, staffData, assignmentsData] = await Promise.all([
        contractsAPI.getAll(),
        staffAPI.getAll(),
        staffAPI.getAllAssignments()
      ]);
      setContracts(contractsData);
      setStaff(staffData.filter(s => s.status === 'active'));
      setAssignments(assignmentsData);
      
      // Fetch workload for current month
      const month = String(currentDate.getMonth() + 1);
      const year = currentDate.getFullYear();
      try {
        const workloadData = await staffAPI.getWorkload(month, year);
        setWorkload(workloadData.staff_workload || []);
      } catch (e) {
        console.warn('Workload fetch failed:', e);
      }
    } catch (error) {
      toast({ title: 'Error loading data', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [currentDate, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const formatDateKey = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    const dateStr = formatDateKey(date);
    return contracts.filter(c => c.event_date === dateStr);
  };

  const getAssignmentsForContract = (contractId) => {
    return assignments.filter(a => a.contract_id === contractId);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const openAssignmentDialog = async (contract) => {
    setSelectedContract(contract);
    setAssignmentForm({
      staff_id: '',
      role: '',
      start_time: contract.event_time || contract.event_start_time || '',
      end_time: contract.event_end_time || '',
      notes: ''
    });
    setBulkAssignMode(false);
    setBulkAssignments([]);
    
    // Fetch staff suggestion
    if (contract.guest_count) {
      try {
        const suggestion = await staffAPI.suggestStaffCount(contract.guest_count);
        setStaffSuggestion(suggestion.suggested);
      } catch (e) {
        console.warn('Staff suggestion fetch failed:', e);
      }
    }
    
    // Check for conflicts on this date
    try {
      const conflictData = await staffAPI.getDateConflicts(contract.event_date);
      setConflicts(conflictData.conflicts || {});
    } catch (e) {
      console.warn('Conflict check failed:', e);
    }
    
    setIsDialogOpen(true);
  };

  const checkStaffAvailability = async (staffId) => {
    if (!selectedContract) return null;
    try {
      const result = await staffAPI.checkAvailability(staffId, selectedContract.event_date);
      return result;
    } catch (e) {
      return null;
    }
  };

  const handleAssignStaff = async () => {
    if (!assignmentForm.staff_id || !assignmentForm.role) {
      toast({ title: 'Please select staff and role', variant: 'destructive' });
      return;
    }

    // Check availability first
    const availability = await checkStaffAvailability(assignmentForm.staff_id);
    if (availability && !availability.is_available) {
      const confirm = window.confirm(
        `⚠️ ${availability.staff_name} already has ${availability.conflict_count} assignment(s) on this date. Assign anyway?`
      );
      if (!confirm) return;
    }

    try {
      const selectedStaff = staff.find(s => s.id === assignmentForm.staff_id);
      await staffAPI.createAssignment({
        contract_id: selectedContract.id,
        staff_id: assignmentForm.staff_id,
        staff_name: selectedStaff?.name || '',
        role: assignmentForm.role,
        event_date: selectedContract.event_date,
        start_time: assignmentForm.start_time || null,
        end_time: assignmentForm.end_time || null,
        notes: assignmentForm.notes || null
      });
      
      toast({ title: 'Staff assigned successfully!' });
      fetchData();
      setAssignmentForm({ staff_id: '', role: '', start_time: assignmentForm.start_time, end_time: assignmentForm.end_time, notes: '' });
    } catch (error) {
      toast({ title: 'Error assigning staff', description: error.message, variant: 'destructive' });
    }
  };

  const addToBulkAssign = () => {
    if (!assignmentForm.staff_id || !assignmentForm.role) {
      toast({ title: 'Please select staff and role', variant: 'destructive' });
      return;
    }
    const selectedStaff = staff.find(s => s.id === assignmentForm.staff_id);
    setBulkAssignments([...bulkAssignments, {
      staff_id: assignmentForm.staff_id,
      staff_name: selectedStaff?.name || '',
      role: assignmentForm.role,
      start_time: assignmentForm.start_time,
      end_time: assignmentForm.end_time,
      notes: assignmentForm.notes
    }]);
    setAssignmentForm({ ...assignmentForm, staff_id: '', role: '', notes: '' });
  };

  const submitBulkAssignments = async () => {
    if (bulkAssignments.length === 0) {
      toast({ title: 'No staff to assign', variant: 'destructive' });
      return;
    }
    
    try {
      const result = await staffAPI.createBulkAssignments(
        selectedContract.id,
        selectedContract.event_date,
        bulkAssignments
      );
      
      if (result.conflict_count > 0) {
        toast({ 
          title: `Assigned ${result.created_count} staff (${result.conflict_count} conflicts skipped)`,
          variant: 'default'
        });
      } else {
        toast({ title: `Successfully assigned ${result.created_count} staff!` });
      }
      
      fetchData();
      setBulkAssignments([]);
      setBulkAssignMode(false);
    } catch (error) {
      toast({ title: 'Error assigning staff', variant: 'destructive' });
    }
  };

  const removeAssignment = async (assignmentId) => {
    if (!window.confirm('Remove this staff assignment?')) return;
    try {
      await staffAPI.deleteAssignment(assignmentId);
      toast({ title: 'Assignment removed' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error removing assignment', variant: 'destructive' });
    }
  };

  const openDayView = async (date) => {
    setSelectedDate(date);
    try {
      const data = await staffAPI.getDayView(formatDateKey(date));
      setDayViewData(data);
      setShowDayView(true);
    } catch (error) {
      toast({ title: 'Error loading day view', variant: 'destructive' });
    }
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      'offsite': 'bg-blue-500',
      'onsite': 'bg-green-500',
      'tournament': 'bg-purple-500',
      'cocktail_coach': 'bg-orange-500'
    };
    return colors[eventType] || 'bg-gray-500';
  };

  const getRoleColor = (role) => {
    const colors = {
      'server': 'bg-blue-100 text-blue-800',
      'bartender': 'bg-purple-100 text-purple-800',
      'chef': 'bg-red-100 text-red-800',
      'kitchen': 'bg-orange-100 text-orange-800',
      'setup': 'bg-green-100 text-green-800',
      'manager': 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const isStaffConflicted = (staffId) => {
    return conflicts[staffId] && conflicts[staffId].length > 1;
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5dc] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f] mx-auto"></div>
          <p className="mt-4 text-[#1e3a5f]">Loading scheduler...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5dc] p-6" data-testid="staff-scheduler-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-[#1e3a5f]">Staff Scheduler</h1>
            <p className="text-gray-600">Assign staff to upcoming events</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center bg-white rounded-lg shadow">
              <Button variant="ghost" onClick={() => navigateMonth(-1)} className="px-3" data-testid="prev-month-btn">
                <ChevronLeft size={20} />
              </Button>
              <span className="px-4 py-2 font-semibold text-[#1e3a5f]">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </span>
              <Button variant="ghost" onClick={() => navigateMonth(1)} className="px-3" data-testid="next-month-btn">
                <ChevronRight size={20} />
              </Button>
            </div>
            <Button onClick={() => setCurrentDate(new Date())} variant="outline" className="border-[#1e3a5f] text-[#1e3a5f]">
              Today
            </Button>
          </div>
        </div>

        {/* Workload Summary Card */}
        <Card className="mb-6" data-testid="workload-summary">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <TrendingUp size={20} />
              Staff Workload - {monthNames[currentDate.getMonth()]}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {workload.slice(0, 6).map((w) => (
                <div key={w.staff_id} className="bg-gray-50 p-3 rounded-lg" data-testid={`workload-${w.staff_id}`}>
                  <p className="font-semibold text-sm text-[#1e3a5f] truncate">{w.staff_name}</p>
                  <p className="text-xs text-gray-500 capitalize">{w.role}</p>
                  <div className="flex justify-between mt-2 text-xs">
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {w.events_count} events
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {w.total_hours}h
                    </span>
                  </div>
                  {w.estimated_earnings > 0 && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <DollarSign size={12} />
                      ${w.estimated_earnings.toFixed(0)}
                    </p>
                  )}
                </div>
              ))}
              {workload.length === 0 && (
                <p className="col-span-full text-gray-500 text-sm">No workload data for this month</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Staff Summary */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Users className="text-[#1e3a5f]" />
                  <span className="font-semibold">{staff.length} Active Staff</span>
                </div>
                <div className="flex gap-2 flex-wrap text-sm">
                  {['server', 'bartender', 'chef', 'kitchen', 'setup', 'manager'].map(role => {
                    const count = staff.filter(s => s.role === role).length;
                    if (count === 0) return null;
                    return (
                      <span key={role} className={`px-2 py-1 rounded ${getRoleColor(role)}`}>
                        {role}: {count}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-blue-500"></span> Offsite
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-green-500"></span> Onsite
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-purple-500"></span> Tournament
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendar Grid */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center font-semibold text-gray-600 py-2">{day}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                const events = day ? getEventsForDate(day) : [];
                const isToday = day && formatDateKey(day) === formatDateKey(new Date());
                const hasEvents = events.length > 0;
                
                return (
                  <div
                    key={`day-${index}`}
                    className={`min-h-[120px] border rounded-lg p-2 ${
                      day ? 'bg-white' : 'bg-gray-100'
                    } ${isToday ? 'ring-2 ring-[#c9a227]' : ''}`}
                  >
                    {day && (
                      <>
                        <div className="flex justify-between items-center mb-1">
                          <span className={`text-sm font-semibold ${isToday ? 'text-[#c9a227]' : 'text-gray-700'}`}>
                            {day.getDate()}
                          </span>
                          {hasEvents && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => openDayView(day)}
                              data-testid={`day-view-btn-${formatDateKey(day)}`}
                            >
                              <Eye size={14} />
                            </Button>
                          )}
                        </div>
                        <div className="space-y-1">
                          {events.slice(0, 3).map(event => {
                            const eventAssignments = getAssignmentsForContract(event.id);
                            return (
                              <div
                                key={event.id}
                                onClick={() => openAssignmentDialog(event)}
                                className={`text-xs p-1 rounded cursor-pointer hover:opacity-80 text-white ${getEventTypeColor(event.event_type)}`}
                                data-testid={`event-${event.id}`}
                              >
                                <div className="font-medium truncate">{event.event_name}</div>
                                <div className="flex items-center justify-between">
                                  <span className="opacity-80">{event.guest_count}g</span>
                                  <span className="flex items-center gap-0.5">
                                    <Users size={10} />
                                    {eventAssignments.length}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                          {events.length > 3 && (
                            <div className="text-xs text-gray-500 text-center">+{events.length - 3} more</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Assignment Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Staff Assignment - {selectedContract?.event_name}</DialogTitle>
              <DialogDescription>Assign staff members to this event</DialogDescription>
            </DialogHeader>
            
            {selectedContract && (
              <div className="space-y-6">
                {/* Event Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Date:</span>
                      <p className="font-semibold">{selectedContract.event_date}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Time:</span>
                      <p className="font-semibold">{selectedContract.event_time || selectedContract.event_start_time || 'TBD'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Guests:</span>
                      <p className="font-semibold">{selectedContract.guest_count}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Location:</span>
                      <p className="font-semibold">{selectedContract.location}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Type:</span>
                      <p className="font-semibold capitalize">{selectedContract.event_type?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Client:</span>
                      <p className="font-semibold">{selectedContract.client_name}</p>
                    </div>
                  </div>
                </div>

                {/* Staff Suggestion */}
                {staffSuggestion && (
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg" data-testid="staff-suggestion">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className="text-blue-600" size={18} />
                      <span className="font-semibold text-blue-800">Recommended Staffing</span>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <span className="px-2 py-1 bg-blue-100 rounded">Servers: {staffSuggestion.servers}</span>
                      <span className="px-2 py-1 bg-purple-100 rounded">Bartenders: {staffSuggestion.bartenders}</span>
                      <span className="px-2 py-1 bg-orange-100 rounded">Kitchen: {staffSuggestion.kitchen}</span>
                      <span className="px-2 py-1 bg-green-100 rounded">Setup: {staffSuggestion.setup}</span>
                      <span className="px-2 py-1 bg-gray-200 rounded font-semibold">Total: {staffSuggestion.total}</span>
                    </div>
                  </div>
                )}

                {/* Current Assignments */}
                <div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-2">
                    Assigned Staff ({getAssignmentsForContract(selectedContract.id).length})
                  </h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {getAssignmentsForContract(selectedContract.id).length === 0 ? (
                      <p className="text-gray-500 text-sm">No staff assigned yet</p>
                    ) : (
                      getAssignmentsForContract(selectedContract.id).map(assignment => (
                        <div key={assignment.id} className="flex items-center justify-between bg-white border rounded p-2">
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded text-xs ${getRoleColor(assignment.role)}`}>
                              {assignment.role}
                            </span>
                            <span className="font-medium">{assignment.staff_name}</span>
                            {isStaffConflicted(assignment.staff_id) && (
                              <AlertTriangle className="text-amber-500" size={14} title="Staff has multiple assignments today" />
                            )}
                            {assignment.start_time && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Clock size={12} />
                                {assignment.start_time} - {assignment.end_time || '?'}
                              </span>
                            )}
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeAssignment(assignment.id)} className="text-red-500 hover:text-red-700">
                            <X size={16} />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Add Staff Form */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-[#1e3a5f]">Add Staff</h3>
                    <Button
                      variant={bulkAssignMode ? "default" : "outline"}
                      size="sm"
                      onClick={() => setBulkAssignMode(!bulkAssignMode)}
                      className={bulkAssignMode ? "bg-[#1e3a5f]" : ""}
                      data-testid="bulk-mode-toggle"
                    >
                      <UserPlus size={16} className="mr-1" />
                      {bulkAssignMode ? 'Bulk Mode ON' : 'Bulk Assign'}
                    </Button>
                  </div>

                  {bulkAssignMode && bulkAssignments.length > 0 && (
                    <div className="mb-4 p-3 bg-green-50 rounded-lg">
                      <p className="text-sm font-medium text-green-800 mb-2">Pending Assignments ({bulkAssignments.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {bulkAssignments.map((ba, idx) => (
                          <span key={`ba-${ba.staff_id}-${ba.role}`} className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded text-xs">
                            {ba.staff_name} ({ba.role})
                            <X size={12} className="cursor-pointer text-red-500" onClick={() => {
                              setBulkAssignments(bulkAssignments.filter((_, i) => i !== idx));
                            }} />
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Staff Member</Label>
                      <Select
                        value={assignmentForm.staff_id}
                        onValueChange={(value) => {
                          const selectedStaff = staff.find(s => s.id === value);
                          setAssignmentForm({
                            ...assignmentForm,
                            staff_id: value,
                            role: selectedStaff?.role || assignmentForm.role
                          });
                        }}
                      >
                        <SelectTrigger data-testid="staff-select">
                          <SelectValue placeholder="Select staff" />
                        </SelectTrigger>
                        <SelectContent>
                          {staff.map(s => {
                            const hasConflict = isStaffConflicted(s.id);
                            return (
                              <SelectItem key={s.id} value={s.id}>
                                <span className="flex items-center gap-2">
                                  {s.name} ({s.role})
                                  {hasConflict && <AlertTriangle className="text-amber-500" size={12} />}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Role for this Event</Label>
                      <Select
                        value={assignmentForm.role}
                        onValueChange={(value) => setAssignmentForm({ ...assignmentForm, role: value })}
                      >
                        <SelectTrigger data-testid="role-select">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="server">Server</SelectItem>
                          <SelectItem value="bartender">Bartender</SelectItem>
                          <SelectItem value="chef">Chef</SelectItem>
                          <SelectItem value="kitchen">Kitchen</SelectItem>
                          <SelectItem value="setup">Setup</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Start Time</Label>
                      <Input
                        type="time"
                        value={assignmentForm.start_time}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, start_time: e.target.value })}
                        data-testid="start-time-input"
                      />
                    </div>
                    <div>
                      <Label>End Time</Label>
                      <Input
                        type="time"
                        value={assignmentForm.end_time}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, end_time: e.target.value })}
                        data-testid="end-time-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={assignmentForm.notes}
                        onChange={(e) => setAssignmentForm({ ...assignmentForm, notes: e.target.value })}
                        placeholder="Special instructions..."
                        rows={2}
                      />
                    </div>
                  </div>
                  
                  {bulkAssignMode ? (
                    <div className="flex gap-2 mt-4">
                      <Button onClick={addToBulkAssign} variant="outline" className="flex-1" data-testid="add-to-bulk-btn">
                        <Plus size={16} className="mr-2" />
                        Add to List
                      </Button>
                      <Button onClick={submitBulkAssignments} className="flex-1 bg-[#1e3a5f] hover:bg-[#2a4a6f]" data-testid="submit-bulk-btn">
                        <Users size={16} className="mr-2" />
                        Assign All ({bulkAssignments.length})
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={handleAssignStaff} className="w-full mt-4 bg-[#1e3a5f] hover:bg-[#2a4a6f]" data-testid="assign-staff-btn">
                      <Plus size={16} className="mr-2" />
                      Assign Staff
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Day View Dialog */}
        <Dialog open={showDayView} onOpenChange={setShowDayView}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar size={20} />
                Day View - {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </DialogTitle>
              <DialogDescription>All events and staff assignments for this day</DialogDescription>
            </DialogHeader>
            
            {dayViewData && (
              <div className="space-y-6">
                {/* Day Summary */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-700">{dayViewData.events_count}</p>
                    <p className="text-xs text-blue-600">Events</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">{dayViewData.total_guests}</p>
                    <p className="text-xs text-green-600">Total Guests</p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-700">{dayViewData.total_staff_assigned}</p>
                    <p className="text-xs text-purple-600">Staff Assigned</p>
                  </div>
                  <div className="bg-amber-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-700">{dayViewData.available_staff?.length || 0}</p>
                    <p className="text-xs text-amber-600">Available Staff</p>
                  </div>
                </div>

                {/* Events Timeline */}
                <div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-3">Events Timeline</h3>
                  <div className="space-y-4">
                    {dayViewData.events?.map((event, idx) => (
                      <Card key={event.id || `evt-${idx}`} className="border-l-4" style={{ borderLeftColor: getEventTypeColor(event.event_type).replace('bg-', '#') }}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="font-semibold text-[#1e3a5f]">{event.event_name}</h4>
                              <p className="text-sm text-gray-500">{event.client_name} • {event.location}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">{event.event_start_time || event.event_time || 'TBD'} - {event.event_end_time || '?'}</p>
                              <p className="text-sm text-gray-500">{event.guest_count} guests</p>
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap gap-2">
                            {event.staff?.length > 0 ? (
                              event.staff.map((s, sIdx) => (
                                <span key={sIdx} className={`px-2 py-1 rounded text-xs ${getRoleColor(s.role)}`}>
                                  {s.staff_name} ({s.role})
                                  {s.start_time && ` ${s.start_time}-${s.end_time || '?'}`}
                                </span>
                              ))
                            ) : (
                              <span className="text-sm text-red-500 flex items-center gap-1">
                                <AlertTriangle size={14} />
                                No staff assigned
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    
                    {dayViewData.events?.length === 0 && (
                      <p className="text-gray-500 text-center py-8">No events scheduled for this day</p>
                    )}
                  </div>
                </div>

                {/* Available Staff */}
                {dayViewData.available_staff?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-3">Available Staff (Not Assigned)</h3>
                    <div className="flex flex-wrap gap-2">
                      {dayViewData.available_staff.map((s, idx) => (
                        <span key={s.id || `staff-${idx}`} className={`px-3 py-1 rounded ${getRoleColor(s.role)}`}>
                          {s.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default StaffScheduler;
